import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { Button, Col, Modal, Row } from 'react-bootstrap'
import { FormattedMessage, injectIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { getSelectedCustomer } from '../../../../_augmentt/selectors/customers'
import { createHubTask, pollTaskStatus } from '../../../crud/integrationHub.crud'
import Spinner from '../../../partials/content/Spinner'
import { TASK_ACTIONS, TASK_TYPES } from '../../../util/hub-task-management/hub.constants'
import { getEmployeeFullName } from '../../../util/table.util'

const propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  // eslint-disable-next-line react/no-unused-prop-types
  onUpdate: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  employees: PropTypes.array,
}

function ReRegisterMfaModal(props) {
  // eslint-disable-next-line react/prop-types
  const { show = false, onHide = () => {}, onUpdate = () => {}, employees = [] } = props ?? {}

  const currentUser = useSelector(({ user }) => user.data)
  const selectedCustomer = useSelector(getSelectedCustomer)
  const selectedIntegration = useSelector(({ integrations }) => integrations.selectedIntegration)

  const STATUS = {
    REVIEW: 'REVIEW',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETED: 'COMPLETED',
  }

  const ERRORS = {
    GLOBAL_ERRORS: 'GLOBAL_ERRORS',
    OPERATIONAL: 'OPERATIONAL',
  }

  const [status, setStatus] = useState(STATUS.REVIEW)
  const [taskErrors, setTaskErrors] = useState([])

  const onApply = async () => {
    setStatus(STATUS.IN_PROGRESS)
    // await onUpdate()

    const parameters = {
      actions: {
        userIds: employees?.map((emp) => emp.employeeIdentifier || emp.employee_identifier),
      },
      tasktype: TASK_TYPES.reRegisterMFA,
      action: TASK_ACTIONS.update,
      customerId: selectedCustomer?.id,
      userId: currentUser?.id,
      organizationId: selectedCustomer?.organization,
    }

    try {
      const { data: { taskId } = {} } = await createHubTask(parameters)

      if (taskId) {
        const { data: { _source: task } = {} } =
          (await pollTaskStatus(taskId, {
            retries: 10,
            timeout: 3000,
          })) ?? {}

        setTaskErrors(JSON.parse(task?.errors || '{}'))
        setStatus(STATUS.COMPLETED)
        // Don't call onUpdate() here - it closes the modal. Call it when user closes the summary screen.
      }
    } catch (error) {
      setTaskErrors({
        OPERATIONAL: { message: error.message },
      })
      setStatus(STATUS.COMPLETED)
    }
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="md"
      aria-labelledby="re-register-mfa-modal"
      className={`engage-modal right ${clsx({ in: show })}`}
    >
      <Modal.Header closeButton className="grey-bg-color-1">
        <Modal.Title id="contained-modal-title-vcenter">
          <FormattedMessage id="ENGAGE.RE_REGISTER_MFA" />
        </Modal.Title>

        {employees?.length === 1 && (
          <p className="mt-2">
            {employees?.[0]?.firstName} {employees?.[0]?.lastName}
          </p>
        )}

        <p className="mt-2">
          {selectedIntegration?.IntegrationApplication?.display_name === 'M365'
            ? 'Microsoft 365'
            : selectedIntegration?.IntegrationApplication?.display_name}
        </p>
      </Modal.Header>
      <Modal.Body>
        {[STATUS.REVIEW].includes(status) && (
          <Row>
            <Col md="12">
              <p>
                <FormattedMessage id="ENGAGE.RE_REGISTER_MFA_DESC" />
              </p>

              <p className="font-bolder mb-1 mt-4">Require re-register MFA for the following employee(s):</p>

              <ul className="font-bolder">
                {employees?.map((employee) => (
                  <li key={employee.id}>{getEmployeeFullName(employee)}</li>
                ))}
              </ul>
            </Col>
          </Row>
        )}

        {[STATUS.IN_PROGRESS].includes(status) && (
          <div className="modal-loading-centered">
            <Spinner size="small" />
            <div className="mt-4 text-strong text-color-secondary">
              <FormattedMessage id="ENGAGE.RE_REGISTER_MFA_IN_PROGRESS" />
            </div>
          </div>
        )}

        {!taskErrors[ERRORS.OPERATIONAL] && [STATUS.COMPLETED].includes(status) && (
          <ul className="ps-0">
            {employees?.map((employee) => {
              const employeeId = employee?.employeeIdentifier || employee?.employee_identifier
              const hasError = !!taskErrors[employeeId]

              return (
                <li key={employee.id || employeeId} className="list-unstyled">
                  {hasError ? (
                    <i className="icon-xl align-text-bottom font-color-high-risk fas fa-circle-xmark me-2" />
                  ) : (
                    <i className="icon-xl align-text-bottom font-color-engage fas fa-circle-check me-2" />
                  )}
                  {getEmployeeFullName(employee)}

                  {hasError && (
                    <div style={{ width: '100%' }} className="font-color-danger">
                      <pre
                        style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '14px' }}
                        className="font-color-danger"
                      >
                        {JSON.stringify(taskErrors[employeeId] || {}, null, 2)}
                      </pre>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        )}

        {taskErrors[ERRORS.OPERATIONAL] && [STATUS.COMPLETED].includes(status) && (
          <div className="font-color-danger mt-1 ps-0">
            <i className="icon-xl align-text-bottom font-color-high-risk fas fa-circle-xmark me-2" />
            <FormattedMessage id="GENERAL.ERROR" />
          </div>
        )}

        {taskErrors[ERRORS.GLOBAL_ERRORS] && [STATUS.COMPLETED].includes(status) && (
          <div className="font-color-danger mt-1 ps-0">
            <i className="icon-xl align-text-bottom font-color-high-risk fas fa-circle-xmark me-2" />
            <FormattedMessage id="GENERAL.ERROR" />
            <pre
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '14px' }}
              className="font-color-danger mt-1"
            >
              {JSON.stringify(taskErrors[ERRORS.GLOBAL_ERRORS] || {}, null, 2)}
            </pre>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="bottom">
        <Button
          onClick={() => {
            if ([STATUS.COMPLETED].includes(status)) {
              // Call onUpdate when closing from summary screen
              onUpdate()
            }
            onHide()
          }}
          variant="secondary"
        >
          <FormattedMessage id={[STATUS.REVIEW].includes(status) ? 'GENERAL.CANCEL' : 'GENERAL.CLOSE'} />
        </Button>

        {[STATUS.REVIEW].includes(status) && (
          <Button
            id="re-register-mfa-submit-btn"
            variant="primary"
            onClick={onApply}
            className="bg-color-engage pull-right"
          >
            <FormattedMessage id="GENERAL.APPLY" />
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

ReRegisterMfaModal.propTypes = propTypes

export default injectIntl(ReRegisterMfaModal)
